import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subject, Subscription, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from "rxjs/operators";
import { CustomerSearchService } from "../../../shared/services/customer-search.service";
import { TriggerModeOption } from "../trigger-mode-radio-group/trigger-mode-radio-group.component";

/**
 * Config payload emitted to the parent FlowTestCreateComponent. The
 * shape matches what `providers/triggerRunners/appInstall.js` reads on
 * the services side.
 */
export interface AppInstallTestConfig {
  device: {
    mode: "generate" | "specify";
    system_name?: "iOS" | "Android";
    system_version?: string;
    make?: string;
    model?: string;
  };
  accountAppDevice: {
    mode: "generate" | "specify";
    allows_push?: boolean;
    location_permission?: 0 | 1 | 2 | 3;
    app_version?: string;
    locale?: string;
  };
  accountAppUser: {
    mode: "none" | "generate" | "clone_existing";
    source_account_app_user_id?: number;
    source_label?: string;
  };
}

interface UserSearchResult {
  id: number;
  label: string;
}

const IOS_MODELS = ["iPhone", "iPad"];
const ANDROID_MAKES = ["Samsung", "Google", "Motorola"];
const LOCATION_PERMISSION_OPTIONS = [
  { value: 0, label: "Not Determined (0)" },
  { value: 1, label: "Denied (1)" },
  { value: 2, label: "While In Use (2)" },
  { value: 3, label: "Always (3)" },
];

@Component({
  selector: "app-app-install-trigger-form",
  templateUrl: "./app-install-trigger-form.component.html",
  styleUrls: ["./app-install-trigger-form.component.scss"],
})
export class AppInstallTriggerFormComponent implements OnInit, OnDestroy {
  @Input() entryEventType: string | null = null;
  @Output() configChange = new EventEmitter<AppInstallTestConfig>();

  form!: FormGroup;

  readonly iosModels = IOS_MODELS;
  readonly androidMakes = ANDROID_MAKES;
  readonly locationPermissionOptions = LOCATION_PERMISSION_OPTIONS;
  readonly deviceModeOptions: TriggerModeOption[] = [
    { value: "generate", label: "Generate random" },
    { value: "specify", label: "Specify" },
  ];
  readonly appStateModeOptions: TriggerModeOption[] = [
    { value: "generate", label: "Generate random" },
    { value: "specify", label: "Specify" },
  ];
  readonly customerModeOptions: TriggerModeOption[] = [
    { value: "none", label: "Anonymous (no customer)" },
    { value: "generate", label: "Generate from catalog" },
    { value: "clone_existing", label: "Clone existing" },
  ];

  userSearchTerm = "";
  userSearchResults: UserSearchResult[] = [];
  userSearchLoading = false;
  selectedUser: UserSearchResult | null = null;

  private userSearch$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private customerSearch: CustomerSearchService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      device: this.fb.group({
        mode: ["generate"],
        system_name: ["iOS"],
        system_version: [""],
        make: [{ value: "Apple", disabled: true }],
        model: ["iPhone"],
      }),
      accountAppDevice: this.fb.group({
        mode: ["generate"],
        allows_push: [true],
        location_permission: [2],
        app_version: ["1.0.0"],
        locale: ["en-US"],
      }),
      accountAppUser: this.fb.group({
        mode: ["generate"],
      }),
    });

    this.subs.push(
      this.form.valueChanges.subscribe(() => this.emit()),
    );

    this.subs.push(
      this.userSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((term) => {
            if (!term || term.trim().length < 2) {
              this.userSearchLoading = false;
              return of({ records: [] });
            }
            this.userSearchLoading = true;
            const payload = this.customerSearch.buildBasePayload({
              query: {
                keyword: { value: term },
                pagination: { page: 0, per_page: 10 },
              },
              context: {
                mode: {
                  registration: "registered",
                  devices: "latest_per_user",
                },
              } as any,
            });
            return this.customerSearch.runSearch(payload).pipe(
              catchError(() => of({ records: [] })),
            );
          }),
        )
        .subscribe((res: any) => {
          const records = Array.isArray(res) ? res : res?.records || [];
          const seen = new Set<number>();
          this.userSearchResults = records
            .map((r: any) => {
              const id = r.account_app_user_id ?? r.appUser?.id;
              if (!id || seen.has(id)) return null;
              seen.add(id);
              const first = r.first_name || r.appUser?.first_name || "";
              const last = r.last_name || r.appUser?.last_name || "";
              const email = r.email || r.appUser?.email || "";
              const name = `${first} ${last}`.trim() || "(no name)";
              const label = email ? `${name} · ${email}` : name;
              return { id, label };
            })
            .filter((x: UserSearchResult | null): x is UserSearchResult => !!x);
          this.userSearchLoading = false;
        }),
    );

    // Emit initial state so the parent has a config snapshot even if the
    // user doesn't touch any control.
    this.emit();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get deviceMode(): string {
    return this.form.get("device.mode")!.value;
  }

  get aadMode(): string {
    return this.form.get("accountAppDevice.mode")!.value;
  }

  get userMode(): string {
    return this.form.get("accountAppUser.mode")!.value;
  }

  onSystemNameChange(systemName: "iOS" | "Android") {
    const deviceGroup = this.form.get("device") as FormGroup;
    if (systemName === "iOS") {
      deviceGroup.patchValue({ make: "Apple", model: "iPhone" });
      deviceGroup.get("make")!.disable({ emitEvent: false });
    } else {
      deviceGroup.get("make")!.enable({ emitEvent: false });
      deviceGroup.patchValue({ make: "Samsung", model: "" });
    }
  }

  onUserSearchInput(value: string) {
    this.userSearchTerm = value;
    this.userSearch$.next(value);
  }

  pickUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchResults = [];
    this.userSearchTerm = user.label;
    this.emit();
  }

  clearUser() {
    this.selectedUser = null;
    this.userSearchTerm = "";
    this.userSearchResults = [];
    this.emit();
  }

  private emit(): void {
    if (!this.form) return;
    const raw = this.form.getRawValue();
    const config: AppInstallTestConfig = {
      device: {
        mode: raw.device.mode,
      },
      accountAppDevice: {
        mode: raw.accountAppDevice.mode,
      },
      accountAppUser: {
        mode: raw.accountAppUser.mode,
      },
    };

    if (raw.device.mode === "specify") {
      config.device.system_name = raw.device.system_name;
      config.device.system_version = raw.device.system_version || undefined;
      config.device.make = raw.device.make || undefined;
      config.device.model = raw.device.model || undefined;
    }

    if (raw.accountAppDevice.mode === "specify") {
      config.accountAppDevice.allows_push = !!raw.accountAppDevice.allows_push;
      config.accountAppDevice.location_permission =
        raw.accountAppDevice.location_permission;
      config.accountAppDevice.app_version =
        raw.accountAppDevice.app_version || undefined;
      config.accountAppDevice.locale =
        raw.accountAppDevice.locale || undefined;
    }

    if (raw.accountAppUser.mode === "clone_existing" && this.selectedUser) {
      config.accountAppUser.source_account_app_user_id = this.selectedUser.id;
      config.accountAppUser.source_label = this.selectedUser.label;
    }

    this.configChange.emit(config);
  }
}
